import { Auth0Error, WebAuth } from 'auth0-js';
import { FormEvent, useCallback, useState } from 'react';
import Heading from '../../components/heading';
import Input from '../../components/input';
import Button from '../../components/button';
import { useGlobalState } from '../globalState/GlobalStateProvider';
import Message, { IMessage } from '../../components/message';
import ButtonGroup from '../../components/buttonGroup';

interface PasswordProps {
  auth0: WebAuth;
}

const Password: React.FC<PasswordProps> = ({ auth0 }: PasswordProps) => {
  const { state, setState } = useGlobalState();
  const { email, passwordHint, connection } = state;

  const [ready, setReady] = useState(true);
  const [password, setPassword] = useState(passwordHint || '');
  const [messages, setMessages] = useState<IMessage | null>(null);

  function passwordCallback(error: Auth0Error | null) {
    if (error) {
      let errorMessage;
      switch (error.code) {
        case 'request_error':
          errorMessage =
            'Het lukt niet om je in te loggen. Probeer het later nog een keer.';
          break;
        case 'invalid_user_password':
          errorMessage = 'Wachtwoord onjuist';
          break;
        default:
          errorMessage = `Er is een fout opgetreden. Kan je het nog een keer proberen? Stuur deze foutcode door als je contact met ons opneemt: ${error.code}`;
          break;
      }
      setMessages({
        type: 'error',
        text: errorMessage,
      });

      setReady(true);
    }
  }

  const passwordResetCallback = useCallback(
    (error: Auth0Error | null) => {
      if (error) {
        setMessages({
          type: 'error',
          text: 'Het lukt niet om je wachtwoord te veranderen. Probeer het later nog een keer.',
        });
      } else {
        setMessages({
          type: 'success',
          text: `We hebben een e-mail gestuurd naar ${email} om je wachtwoord te veranderen.`,
        });
      }

      setReady(true);
    },
    [email],
  );

  const requestPasswordReset = useCallback(() => {
    if (email && connection && ready) {
      setReady(false);
      auth0.changePassword(
        {
          email,
          connection,
        },
        passwordResetCallback,
      );
    }
  }, [email, connection, ready, auth0, passwordResetCallback]);

  function authorizePassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (ready) {
      setReady(false);
      auth0.login(
        {
          email,
          password,
          realm: connection,
        },
        passwordCallback,
      );
    }
  }

  const handleBack = useCallback(() => {
    setState({
      ...state,
      page: 'identifier',
    });
  }, [setState, state]);

  return (
    <>
      <Heading title="Vul je wachtwoord in" />
      <Message message={messages} />
      <form onSubmit={authorizePassword}>
        <Input
          type="email"
          readOnly
          label="E-mail"
          name="email"
          value={email}
          onEdit={handleBack}
        />
        <Input
          type="password"
          onChange={(e) => setPassword(e.target.value)}
          value={password}
          label="Wachtwoord"
          name="password"
          autoComplete="current-password"
          autoFocus
        />
        <ButtonGroup>
          <Button type="submit" formReady={ready}>
            Volgende
          </Button>
          <Button
            type="button"
            formReady={ready}
            onClick={requestPasswordReset}
            look="secondary"
          >
            Wachtwoord vergeten
          </Button>
        </ButtonGroup>
      </form>
    </>
  );
};

export default Password;
